// Carregando, erro e sem internet são estados diferentes de "vazio".
// "Nenhuma cliente ainda" só aparece quando o servidor respondeu e a lista veio vazia:
// falha de conexão nunca se disfarça de tela vazia (a dona cadastraria ficha em dobro).
import { Component, useEffect, useState, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { ApiError } from '../api.ts';

// blocos calmos no formato do que vai chegar; sem brilho correndo (cansa e gasta bateria)
export function Skeleton({ rows = 4, kind = 'list' }: { rows?: number; kind?: 'list' | 'card' | 'sheet' }) {
  return (
    <div className={`sk sk-${kind}`} role="status" aria-label="Carregando">
      {kind === 'card' && <span className="sk-b sk-card" />}
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} className="sk-row"><span className="sk-b sk-dot" /><span className="sk-lines"><span className="sk-b" style={{ width: `${62 - (i % 3) * 12}%` }} /><span className="sk-b sk-thin" style={{ width: `${40 - (i % 2) * 10}%` }} /></span></span>
      ))}
    </div>
  );
}

export function useOnline() {
  const [on, setOn] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const up = () => setOn(true), down = () => setOn(false);
    window.addEventListener('online', up); window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);
  return on;
}

// erro de carregamento: diz o que houve, garante que nada se perdeu e oferece tentar de novo
export function Failed({ error, retry, compact = false }: { error: unknown; retry?: () => unknown; compact?: boolean }) {
  const online = useOnline();
  const offline = !online || (error instanceof ApiError && error.code === 'OFFLINE');
  const denied = error instanceof ApiError && error.status === 403;
  const title = offline ? 'Sem internet agora' : denied ? 'Seu perfil não vê esta parte' : 'Não deu para carregar';
  const text = offline ? 'Assim que a conexão voltar, a tela atualiza sozinha. Nada foi perdido.'
    : denied ? 'Peça para a dona liberar no perfil de acesso, se precisar.'
    : 'O salão continua salvo. Tente de novo em alguns segundos.';
  return (
    <div className={'failed' + (compact ? ' compact' : '')} role="alert">
      <i className="ill aguarde" aria-hidden="true" />
      <b>{title}</b>
      <p>{text}</p>
      {retry && !denied && <button className="cta ghost" onClick={() => retry()}>Tentar de novo</button>}
    </div>
  );
}

// porta única para dados: carregando → esqueleto; erro sem dados → Failed; com dados → conteúdo.
// Se já havia dados e a atualização falhou, mostra os dados com um aviso discreto, sem apagar a tela.
export function Gate<T>({ q, children, skeleton, rows, compact }: { q: UseQueryResult<T>; children: (data: T) => ReactNode; skeleton?: 'list' | 'card' | 'sheet'; rows?: number; compact?: boolean }) {
  if (q.data !== undefined) {
    return <>{q.isError && <p className="stale" role="status">Não deu para atualizar agora. Mostrando o que já estava na tela.</p>}{children(q.data)}</>;
  }
  if (q.isError) return <Failed error={q.error} retry={() => q.refetch()} compact={compact} />;
  return <Skeleton kind={skeleton} rows={rows} />;
}

// rede de segurança: se uma tela quebrar, o resto do app continua de pé
export class Boundary extends Component<{ children: ReactNode; resetKey?: string }, { err: Error | null; key?: string }> {
  state: { err: Error | null; key?: string } = { err: null, key: this.props.resetKey };
  static getDerivedStateFromError(err: Error) { return { err }; }
  static getDerivedStateFromProps(p: { resetKey?: string }, s: { err: Error | null; key?: string }) {
    return p.resetKey !== s.key ? { err: null, key: p.resetKey } : null;
  }
  componentDidCatch(err: Error) { console.error('tela quebrou', err); }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="failed" role="alert">
        <i className="ill aguarde" aria-hidden="true" />
        <b>Esta tela travou</b>
        <p>O resto do app continua funcionando e nada foi perdido. Recarregue para abrir de novo.</p>
        <button className="cta ghost" onClick={() => window.location.reload()}>Recarregar</button>
      </div>
    );
  }
}
